/* ==========================================================================
   Unseen Brewing Co. — store-map.js
   Carte interactive des points de vente (MapLibre GL JS + OpenFreeMap).
   Fond de carte vectoriel épuré : libre, sans clé API ni quota.
   Lit les points depuis un îlot JSON, pose des marqueurs, ajuste le cadrage.
   ========================================================================== */

/* Fonds de carte OpenFreeMap — styles vectoriels libres, sans clé ni limite.
   (CARTO, utilisé auparavant, impose désormais une clé API et retire ses
   tuiles raster : d'où la bascule vers OpenFreeMap.) */
const MAP_STYLES = {
  // Clair, minimaliste, très épuré — recommandé (raccord charte Unseen)
  positron: 'https://tiles.openfreemap.org/styles/positron',
  // Doux et légèrement coloré
  bright: 'https://tiles.openfreemap.org/styles/bright',
  // Plus détaillé (relief, points d'intérêt)
  liberty: 'https://tiles.openfreemap.org/styles/liberty',
};

/* Échappe le HTML — évite toute injection dans les popups de la carte. */
function esc(value) {
  return String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));
}

/* N'autorise que les URL http(s) — bloque javascript:, data:, etc. */
function safeUrl(url) {
  return /^https?:\/\//i.test(url || '') ? url : '';
}

/* Marqueur : le wrapper est positionné par MapLibre (il y écrit un transform),
   la pastille reste dans un enfant pour garder sa rotation CSS. */
function makePin(highlight) {
  const wrap = document.createElement('div');
  wrap.className = 'store-pin';
  const dot = document.createElement('span');
  dot.className = 'store-pin__dot';
  if (highlight) dot.style.background = '#D64541';
  wrap.appendChild(dot);
  return wrap;
}

function initOne(el) {
  const data = document.getElementById(el.dataset.pointsId);
  if (!data) return;

  let points = [];
  try {
    points = JSON.parse(data.textContent);
  } catch (e) {
    return;
  }

  const centerLat = parseFloat(el.dataset.lat) || 50.6;
  const centerLng = parseFloat(el.dataset.lng) || 4.7;
  const zoom = parseInt(el.dataset.zoom, 10) || 8;
  const style = MAP_STYLES[el.dataset.mapStyle] || MAP_STYLES.positron;

  // MapLibre exige WebGL. Sans support, on masque le bloc plutôt que de
  // laisser un message d'erreur au milieu de la page.
  let map;
  try {
    map = new maplibregl.Map({
      container: el,
      style: style,
      center: [centerLng, centerLat],
      zoom: zoom,
      scrollZoom: false,
      dragRotate: false,
      pitchWithRotate: false,
    });
  } catch (e) {
    el.hidden = true;
    return;
  }

  map.touchZoomRotate.disableRotation();
  map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-left');

  const coords = [];
  points.forEach((p) => {
    if (typeof p.lat !== 'number' || typeof p.lng !== 'number') return;

    const lines = [`<strong>${esc(p.name)}</strong>`];
    if (p.type) lines.push(esc(p.type));
    if (p.address) lines.push(esc(p.address));
    const url = safeUrl(p.url);
    if (url) lines.push(`<a href="${esc(url)}" target="_blank" rel="noopener">Voir</a>`);

    new maplibregl.Marker({ element: makePin(p.highlight), anchor: 'bottom' })
      .setLngLat([p.lng, p.lat])
      .setPopup(
        new maplibregl.Popup({ offset: 30, closeButton: false }).setHTML(lines.join('<br>'))
      )
      .addTo(map);

    coords.push([p.lng, p.lat]);
  });

  // Cadrage sur les points. À faire une fois la carte chargée : avant ça, le
  // conteneur (aspect-ratio CSS) n'a pas toujours sa taille finale et le zoom
  // calculé serait trop large.
  map.on('load', () => {
    map.resize();
    if (coords.length > 1) {
      const bounds = coords.reduce(
        (acc, c) => acc.extend(c),
        new maplibregl.LngLatBounds(coords[0], coords[0])
      );
      map.fitBounds(bounds, { padding: 48, maxZoom: 12, duration: 0 });
    } else if (coords.length === 1) {
      map.jumpTo({ center: coords[0], zoom: 12 });
    }
  });

  // Le zoom molette ne s'active qu'après un clic (évite de piéger le scroll)
  map.on('click', () => map.scrollZoom.enable());
  el.addEventListener('mouseleave', () => map.scrollZoom.disable());
}

let attempts = 0;

function init() {
  const maps = document.querySelectorAll('[data-store-map]');
  if (!maps.length) return;
  if (typeof maplibregl === 'undefined') {
    // MapLibre pas encore chargé : on réessaie, sans boucler indéfiniment
    attempts += 1;
    if (attempts > 50) return;
    window.setTimeout(init, 200);
    return;
  }
  maps.forEach(initOne);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
